import React, { useCallback } from "react";
import { useSlate } from "slate-react";
import { Editor, Transforms, Element as SlateElement } from "slate";
import { CustomText } from "../../../components/Slate/CustomText";
import {
  BoldIcon,
  H1Icon,
  H2Icon,
  H3Icon,
  ItalicIcon,
  UnderlineIcon,
} from "@heroicons/react/20/solid";
import { BlockType } from "../../../utils/nodeUtils";

const Toolbar: React.FC = () => {
  const editor = useSlate();

  return (
    <div className="flex w-full gap-2 p-2 border-b bg-gray-50 dark:bg-gray-800">
      <ToolbarButton
        format="bold"
        editor={editor}
        icon={<BoldIcon className="size-4" />}
      />
      <ToolbarButton
        format="italic"
        editor={editor}
        icon={<ItalicIcon className="size-4" />}
      />
      <ToolbarButton
        format="underline"
        editor={editor}
        icon={<UnderlineIcon className="size-4" />}
      />
      <ToolbarButton
        format="heading-one"
        editor={editor}
        icon={<H1Icon className="size-4" />}
      />
      <ToolbarButton
        format="heading-two"
        editor={editor}
        icon={<H2Icon className="size-4" />}
      />
      <ToolbarButton
        format="heading-three"
        editor={editor}
        icon={<H3Icon className="size-4" />}
      />
    </div>
  );
};

// **Reusable Toolbar Button**
const ToolbarButton: React.FC<{
  format: BlockType | keyof Omit<CustomText, "text">;
  editor: Editor;
  icon: JSX.Element;
}> = ({ format, editor, icon }) => {
  const isActive = isFormatActive(editor, format);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (["bold", "italic", "underline"].includes(format)) {
        toggleMark(editor, format as keyof Omit<CustomText, "text">);
      } else {
        toggleBlock(editor, format as BlockType);
      }
    },
    [editor, format],
  );

  return (
    <button
      className={`rounded-md p-2 transition ${
        isActive ? "bg-primary text-white" : "text-gray-700 dark:text-gray-300"
      } hover:bg-gray-200 dark:hover:bg-gray-700`}
      onMouseDown={handleMouseDown}
      aria-label={format}
    >
      {icon}
    </button>
  );
};

// **Toggle Text Formatting (Bold, Italic, Underline)**
const toggleMark = (editor: Editor, format: keyof Omit<CustomText, "text">) => {
  const isActive = Editor.marks(editor)?.[format] === true;
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// **Toggle Block (H1, H2)**
const toggleBlock = (editor: Editor, format: BlockType) => {
  const isActive = isFormatActive(editor, format);
  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : format,
  } as Partial<SlateElement>);
};

// **Check if Format is Active**
const isFormatActive = (
  editor: Editor,
  format: BlockType | keyof Omit<CustomText, "text">,
) => {
  if (["bold", "italic", "underline"].includes(format)) {
    return (
      Editor.marks(editor)?.[format as keyof Omit<CustomText, "text">] === true
    );
  }

  const match = Editor.nodes(editor, {
    match: (n) => (n as any).type === format,
  }).next().value;

  return !!match;
};

export default Toolbar;
